import { Text, Textarea } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';

import {
  Button,
  IconButton,
  Popover,
  RevelioTooltip,
  Text as UIText,
  useDisclosure,
} from '@revelio/ui';

import {
  FilledStarIcon,
  QuestionOutlineIcon,
} from '../../../../../shared/ui/icons';
import { getTextAssist } from '../../../api/build-a-dash-ai-gen-api';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getAvailableMetrics } from '../../ChartConfig/utils/metric-mapping';
import { DashboardErrorBoundary } from '../../DashboardErrorBoundary';
import type { MultiMetricDataMap } from '../../DataProvider/MultiMetricProvider';
import { TextMultiMetricProvider } from '../../DataProvider/TextMultiMetricProvider';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import { useReportParameters } from '../../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import type { UpdateChartContentParams } from '../../TextChartContentProvider/TextChartContentProvider';
import { ChartError } from '../ChartError';
import { ChartLoader } from '../ChartLoader';
import { ChartProvider } from '../ChartProvider';
import { ChartMenuDataProvider } from '../chart-menu/ChartMenuContext';
import { processMultiMetricData } from '../utils/process-multi-metric-data';
import styles from './TextContentEditor.module.css';
import { TextRenderer } from './TextRenderer';
import layoutStyles from './text-content-editor.module.css';

// The modal brings in CodeMirror and the Handlebars language support, so it
// loads when this panel mounts rather than at startup.
const loadTextContentEditorModal = () => import('./TextContentEditorModal');

const TextContentEditorModal = lazy(() =>
  loadTextContentEditorModal().then((module) => ({
    default: module.TextContentEditorModal,
  }))
);

type TextContentEditorProps = {
  item: PositionedItem;
  value: string;
  onChange: (value: string) => void;
  primaryFilterId: PrimaryFilterId;
};

type TextPreviewProps = {
  text: string;
  item: PositionedItem;
  primaryFilterId: PrimaryFilterId;
  onMultiDataChange?: (multiData: MultiMetricDataMap) => void;
};

type TextPreviewInnerProps = {
  text: string;
  item: PositionedItem;
  primaryFilterId: PrimaryFilterId;
  onMultiDataChange?: (multiData: MultiMetricDataMap) => void;
};

function SyncMultiDataForValidation({
  multiData,
  onMultiDataChange,
}: {
  multiData: MultiMetricDataMap;
  onMultiDataChange?: (multiData: MultiMetricDataMap) => void;
}) {
  useEffect(() => {
    onMultiDataChange?.(multiData);
  }, [multiData, onMultiDataChange]);
  return null;
}

const TextPreviewInner = ({
  text,
  item,
  primaryFilterId,
  onMultiDataChange,
}: TextPreviewInnerProps) => {
  const reportParameters = useReportParameters();
  const { activeFilterMap } = useFilterContext();
  const handleProcessedContent = useCallback(
    (_params: UpdateChartContentParams) => undefined,
    []
  );

  return (
    <ChartMenuDataProvider>
      <TextMultiMetricProvider primaryDimension={primaryFilterId} text={text}>
        {({ data, status }) => {
          if (status === DataProviderQueryStatus.Idle) {
            return <ChartLoader />;
          }

          if (status === DataProviderQueryStatus.Fetching) {
            return <ChartLoader />;
          }

          if (status === DataProviderQueryStatus.Error) return <ChartError />;

          if (!text) return null;

          const byMetric = data?.byMetric;
          const multiData: MultiMetricDataMap | undefined = byMetric
            ? processMultiMetricData({
                byMetric,
                plotParameters: item?.plot?.plotParameters ?? [],
                reportParameters,
                activeFilterMap,
              })
            : byMetric;

          return (
            <>
              {multiData ? (
                <SyncMultiDataForValidation
                  multiData={multiData}
                  onMultiDataChange={onMultiDataChange}
                />
              ) : null}
              <TextRenderer
                content={text}
                multiData={multiData}
                color={item.plot?.color}
                item={item}
                onProcessedContent={handleProcessedContent}
              />
            </>
          );
        }}
      </TextMultiMetricProvider>
    </ChartMenuDataProvider>
  );
};

const TextPreview = ({
  text,
  item,
  primaryFilterId,
  onMultiDataChange,
}: TextPreviewProps) => {
  return (
    <TextPreviewInner
      text={text}
      item={{
        ...item,
        plot: { ...item.plot, textHtml: text },
      }}
      primaryFilterId={primaryFilterId}
      onMultiDataChange={onMultiDataChange}
    />
  );
};

type EditorControlsProps = {
  text: string;
  onTextChange: (text: string) => void;
  primaryFilterId: PrimaryFilterId;
};

const EditorControls = ({
  text,
  onTextChange,
  primaryFilterId,
}: EditorControlsProps) => {
  const queryClient = useQueryClient();
  const [aiPrompt, setAiPrompt] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const availableGroupedMetrics = getAvailableMetrics({
    primaryFilterId,
    chartType: 'text',
  });

  const availableMetricHints: Array<{ value: string; description: string }> =
    [];

  for (const groupedMetric of availableGroupedMetrics) {
    const groupLabel = groupedMetric.label;
    const options = groupedMetric.options;
    for (const opt of options) {
      const optValue = opt.value;
      const description = groupLabel
        ? `${optValue} for ${groupLabel}`
        : optValue;
      availableMetricHints.push({ value: optValue, description });
    }
  }

  const textAssistQueryKey = ['text-assist-modal', primaryFilterId];
  const {
    isFetching: isAiFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: textAssistQueryKey,
    queryFn: async ({ signal }) => {
      return await getTextAssist(
        { template: text, prompt: aiPrompt, metrics: availableMetricHints },
        { signal }
      );
    },
    enabled: false,
    retry: false,
  });

  const handleAiAssist = async () => {
    try {
      const result = await refetch();
      const nextTemplate = result.data?.template;
      if (nextTemplate) {
        onTextChange(nextTemplate);
        setIsPopoverOpen(false);
      }
    } catch {
      // If aborted/canceled, ignore.
    }
  };

  const handleAbortAiAssist = async () => {
    await queryClient.cancelQueries({ queryKey: textAssistQueryKey });
  };

  return (
    <div className={layoutStyles.controls}>
      <RevelioTooltip label="Template syntax help">
        <IconButton
          aria-label="Template syntax help"
          icon={<QuestionOutlineIcon />}
          size="sm"
          variant="outlined"
          onClick={() => {
            window.open(
              '/help/text-chart',
              '_blank',
              'width=900,height=800,scrollbars=yes'
            );
          }}
        />
      </RevelioTooltip>
      <Popover
        placement="bottom-end"
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        className={styles.aiAssist}
        trigger={
          <IconButton
            aria-label="AI assist"
            icon={<FilledStarIcon />}
            size="sm"
            variant="outlined"
          />
        }
      >
        <div className={layoutStyles.aiAssistPanel}>
          <Text fontSize="sm" fontWeight={600} mb={2}>
            AI Assist
          </Text>
          <UIText
            fontSize="x-sm"
            tone="secondary"
            className={styles.aiAssistHint}
          >
            Describe what you want.
          </UIText>
          <Textarea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder="e.g. rewrite this intro to be more concise"
            rows={3}
            fontSize="sm"
            resize="none"
            mb={2}
            isDisabled={isAiFetching}
          />
          <div className={layoutStyles.aiAssistActions}>
            {!isAiFetching && (
              <Button
                size="sm"
                variant="ghost"
                color="secondary"
                onClick={() => setAiPrompt('')}
                disabled={isAiFetching}
              >
                Clear
              </Button>
            )}
            {isAiFetching && (
              <Button
                size="sm"
                variant="outlined"
                onClick={handleAbortAiAssist}
                disabled={!isAiFetching}
              >
                Abort
              </Button>
            )}
            <Button
              size="sm"
              color="primary"
              onClick={handleAiAssist}
              loading={isAiFetching}
              disabled={isAiFetching}
            >
              Apply
            </Button>
          </div>
          {error ? (
            <Text fontSize="xs" color="red.500" mt={2}>
              Failed to generate. Please try again.
            </Text>
          ) : null}
        </div>
      </Popover>
    </div>
  );
};

export const TextContentEditor = ({
  item,
  value,
  onChange,
  primaryFilterId,
}: TextContentEditorProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [validationMultiData, setValidationMultiData] = useState<
    MultiMetricDataMap | undefined
  >(undefined);

  // Fetch the chunk while the panel is showing so the first open does not wait
  // on it. A failed fetch is dropped here; opening the editor requests it again.
  useEffect(() => {
    loadTextContentEditorModal().catch(() => undefined);
  }, []);

  return (
    <ChartProvider item={item}>
      {isOpen ? (
        <DashboardErrorBoundary context="TextContentEditor">
          <Suspense fallback={null}>
            <TextContentEditorModal
              isOpen
              onClose={onClose}
              startingText={value}
              onSave={onChange}
              validationMultiData={validationMultiData}
              renderPreview={(text) => (
                <TextPreview
                  text={text}
                  item={item}
                  primaryFilterId={primaryFilterId}
                  onMultiDataChange={setValidationMultiData}
                />
              )}
              renderControls={(text, onTextChange) => (
                <EditorControls
                  text={text}
                  onTextChange={onTextChange}
                  primaryFilterId={primaryFilterId}
                />
              )}
            />
          </Suspense>
        </DashboardErrorBoundary>
      ) : null}
      <Button fullWidth variant="outlined" onClick={onOpen}>
        Open Content Editor
      </Button>
    </ChartProvider>
  );
};
